/**
 * DemoActionModal & useDemoGuard
 *
 * Implements Approach A (Read-Only Sandbox with Guided Simulation):
 * Intercepts mutating actions (create, edit, delete, send messages) in demo mode
 * and presents a guided modal inviting the user to start their 14-day live operational trial.
 */
import { useEffect, useState, useCallback } from 'react'
import { SparklesIcon, XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline'
import { useAuthStore, useVerticalConfig } from '@lipaflow/shared'
import { isDemoTenant } from '../../lib/onboardingRouting'
import DemoGoLiveButton from './DemoGoLiveButton'

export const DEMO_ACTION_EVENT = 'lipaflow:demo-action-blocked'


export interface DemoActionEventDetail {
  actionName?: string
  message?: string
}

/**
 * Dispatch this event or call guardDemoAction to block write operations in demo mode
 * and open the Guided Simulation modal.
 */
export function triggerDemoActionGuard(detail?: DemoActionEventDetail) {
  window.dispatchEvent(new CustomEvent(DEMO_ACTION_EVENT, { detail }))
}

/**
 * Hook to guard click handlers or submit handlers in demo mode.
 */
export function useDemoGuard() {
  const tenant = useAuthStore((s) => s.tenant)
  const isDemo = isDemoTenant(tenant)

  const guard = useCallback(
    (actionName: string, executeIfLive: () => void) => {
      if (isDemo) {
        triggerDemoActionGuard({ actionName })
        return
      }
      executeIfLive()
    },
    [isDemo],
  )

  return { isDemo, guard }
}

export default function DemoActionModal() {
  const [isOpen, setIsOpen] = useState(false)
  const [actionLabel, setActionLabel] = useState<string>('')
  const { labels } = useVerticalConfig()

  useEffect(() => {
    const handleBlocked = (e: Event) => {
      const customEvent = e as CustomEvent<DemoActionEventDetail>
      setActionLabel(customEvent.detail?.actionName || '')
      setIsOpen(true)
    }

    window.addEventListener(DEMO_ACTION_EVENT, handleBlocked)
    return () => window.removeEventListener(DEMO_ACTION_EVENT, handleBlocked)
  }, [])

  if (!isOpen) return null

  const institutionLabel = labels.institution || 'Organization'

  // "Go live" creates a fresh workspace and moves this login to it; sending
  // the user to /onboarding inside the demo would only loop, since every save
  // there is refused.

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-scrim/50 backdrop-blur-xs animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby="demo-modal-title"
    >
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl border border-stone-200">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-teal-50 rounded-xl text-teal-600">
              <SparklesIcon className="w-6 h-6" />
            </div>
            <div>
              <h3 id="demo-modal-title" className="font-outfit text-h3 text-stone-900">
                {actionLabel ? `Simulate "${actionLabel}"` : 'Ready to add your own data?'}
              </h3>
              <p className="text-stone-500 font-inter text-body-sm">
                Interactive Demo Preview Mode
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="text-stone-400 hover:text-stone-600 p-1.5 rounded-lg transition-colors"
            aria-label="Close dialog"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4 mb-6 text-stone-600 font-inter text-body-sm">
          <p>
            You are browsing a <strong>Read-Only Showcase</strong> pre-seeded with sample records to
            keep the experience clean and consistent for all evaluators.
          </p>
          <p>
            To create real records, send reminders and collect payments, go live: we create a clean
            workspace for your {institutionLabel.toLowerCase()} and you keep the same sign-in. Your{' '}
            <strong>14-Day Free Operational Trial</strong> starts once setup is done and you are live.
          </p>

          <div className="p-4 bg-stone-50 rounded-xl border border-stone-200/70 space-y-2.5">
            <div className="flex items-center gap-2 text-stone-700">
              <CheckCircleIcon className="w-4 h-4 text-teal-600 flex-shrink-0" />
              <span>Full private workspace customized with your {institutionLabel.toLowerCase()} name</span>
            </div>
            <div className="flex items-center gap-2 text-stone-700">
              <CheckCircleIcon className="w-4 h-4 text-teal-600 flex-shrink-0" />
              <span>CSV bulk import for your actual clients, fee structures, and units</span>
            </div>
            <div className="flex items-center gap-2 text-stone-700">
              <CheckCircleIcon className="w-4 h-4 text-teal-600 flex-shrink-0" />
              <span>Live M-Pesa collection and automated parent/tenant statements</span>
            </div>
          </div>
        </div>

        <div className="flex items-start justify-end gap-3 pt-2 border-t border-stone-100">
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="px-4 py-2 text-stone-600 hover:text-stone-800 text-body-sm font-medium transition-colors"
          >
            Keep Browsing Demo
          </button>
          <DemoGoLiveButton className="px-5 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-body-sm font-semibold transition-colors shadow-sm disabled:opacity-60" />
        </div>
      </div>
    </div>
  )
}
